import type { FC, PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useClientStoredResourcesContext } from '@pflegenavi/frontend/client-stored-resources-context';
import { SCOPE_KINDS } from '@pflegenavi/frontend/routing';
import type { ScopeKind } from '@pflegenavi/frontend/routing';
import type { LegalEntity, NursingHome, Tenant } from './types';
import { useStoredSelection } from './useStoredSelection';

const STORAGE_KEY = 'scope';

interface StoredScope {
  kind: ScopeKind;
}

interface ScopeContextProps extends StoredScope {
  nursingHomeList: NursingHome[];
  selectedNursingHome?: NursingHome;
  setNursingHomeList: (list: NursingHome[]) => Promise<void>;
  selectNursingHome: (nursingHome: NursingHome) => Promise<void>;

  legalEntityList: LegalEntity[];
  selectedLegalEntity?: LegalEntity;
  setLegalEntityList: (list: LegalEntity[]) => Promise<void>;
  selectLegalEntity: (legalEntity: LegalEntity) => Promise<void>;

  /**
   * Every legal entity the employee can see, for name lookups only. Scope
   * selection uses `legalEntityList`, which is filtered down to the entities
   * the employee is admin or manager in (ENG-2503) - a nursing home still needs
   * to show the name of the entity it belongs to either way.
   */
  knownLegalEntities: LegalEntity[];
  setKnownLegalEntities: (list: LegalEntity[]) => void;

  // There is only ever one tenant, so it has no list/selection pair.
  tenant?: Tenant;
  setTenant: (tenant: Tenant | undefined) => void;

  setScope: (scope: StoredScope) => void;

  /**
   * Whether the stored scope has been read back yet. It arrives asynchronously
   * and `kind` reads as the nursing home default until it does, so anything
   * that would act on the scope - a redirect, say - has to wait for this rather
   * than trust the first render.
   */
  isScopeRestored: boolean;
}

const DEFAULT_SCOPE: StoredScope = { kind: 'nursing_home' };

const isStoredScope = (value: unknown): value is StoredScope =>
  SCOPE_KINDS.includes((value as StoredScope)?.kind);

export const ScopeContext = createContext<ScopeContextProps | undefined>(
  undefined
);

const noop = (): void => undefined;
const asyncNoop = (): Promise<void> => Promise.resolve();

// No provider outside nursing-home-web, so the scope falls back to the
// nursing home those apps already assume.
export const FALLBACK_SCOPE: ScopeContextProps = {
  ...DEFAULT_SCOPE,
  nursingHomeList: [],
  legalEntityList: [],
  knownLegalEntities: [],
  setNursingHomeList: asyncNoop,
  selectNursingHome: asyncNoop,
  setLegalEntityList: asyncNoop,
  selectLegalEntity: asyncNoop,
  setKnownLegalEntities: noop,
  setTenant: noop,
  setScope: noop,
  // Nothing to restore without a provider, so the default is final.
  isScopeRestored: true,
};

export const useScope = (): ScopeContextProps =>
  useContext(ScopeContext) ?? FALLBACK_SCOPE;

export const useScopeFilter = (): {
  nursingHomeId?: string;
  legalEntityId?: string;
} => {
  const { kind, selectedNursingHome, selectedLegalEntity } = useScope();

  return useMemo(() => {
    switch (kind) {
      case 'nursing_home':
        return { nursingHomeId: selectedNursingHome?.id };
      case 'legal_entity':
        return { legalEntityId: selectedLegalEntity?.id };
      case 'tenant':
        return {};
    }
  }, [kind, selectedNursingHome?.id, selectedLegalEntity?.id]);
};

export const useIsScopeReady = (): boolean =>
  Object.values(useScopeFilter()).every(Boolean);

/**
 * The legal entity in force at the current scope: the selected one at legal
 * entity scope, the selected home's one at nursing home scope. Endpoints that
 * are keyed by legal entity at every scope (e.g. the Pliant transaction list)
 * need it resolved either way. `undefined` at tenant scope, which spans several
 * legal entities and so cannot name one.
 *
 * @example
 * const legalEntityId = useScopeLegalEntityId(); // '3f1c...'
 */
export const useScopeLegalEntityId = (): string | undefined => {
  const { kind, selectedNursingHome, selectedLegalEntity } = useScope();

  switch (kind) {
    case 'nursing_home':
      return selectedNursingHome?.legalEntityId;
    case 'legal_entity':
      return selectedLegalEntity?.id;
    case 'tenant':
      return undefined;
  }
};

/**
 * True while a single nursing home is selected, i.e. not at legal entity or
 * tenant scope. Use it to hide nursing-home-bound UI (quick actions, the
 * nursing home status menu, the employee mode selector) at the wider scopes,
 * where it would act on the last selected home instead of what is shown.
 */
export const useIsNursingHomeScope = (): boolean =>
  useScope().kind === 'nursing_home';

/**
 * The nursing homes the current scope covers, for pickers that narrow within it.
 * Kept here because branching on the scope kind is confined to this package - a
 * page would otherwise have to reach for the whole tenant list and re-derive the
 * narrowing itself.
 */
export const useScopedNursingHomes = (): NursingHome[] => {
  const { kind, nursingHomeList, selectedNursingHome, selectedLegalEntity } =
    useScope();

  return useMemo(() => {
    switch (kind) {
      case 'nursing_home':
        return selectedNursingHome ? [selectedNursingHome] : [];
      case 'legal_entity':
        return nursingHomeList.filter(
          (nursingHome) => nursingHome.legalEntityId === selectedLegalEntity?.id
        );
      case 'tenant':
        return nursingHomeList;
    }
  }, [kind, nursingHomeList, selectedNursingHome, selectedLegalEntity?.id]);
};

interface ScopeProviderProps extends PropsWithChildren {
  /**
   * Optional function to get query parameters, so a link can pin the scope
   * (e.g. the welcome link's `nh_id`). If not provided, URL-based scope
   * selection is disabled.
   */
  getQueryParam?: (param: string) => string | null;
}

export const ScopeProvider: FC<ScopeProviderProps> = ({
  children,
  getQueryParam,
}) => {
  const resources = useClientStoredResourcesContext();

  const [scope, setScopeInternal] = useState<StoredScope>(DEFAULT_SCOPE);
  const [isScopeRestored, setIsScopeRestored] = useState(false);
  const [tenant, setTenant] = useState<Tenant | undefined>(undefined);
  const [knownLegalEntities, setKnownLegalEntities] = useState<LegalEntity[]>(
    []
  );

  const applyScope = useCallback(
    (updater: (current: StoredScope) => StoredScope) => {
      setScopeInternal((current) => {
        const next = updater(current);
        if (next !== current) {
          resources.set(STORAGE_KEY, JSON.stringify(next));
        }
        return next;
      });
    },
    [resources]
  );

  const urlNursingHomeId = getQueryParam?.('nh_id') ?? null;
  // A `nh_id` link is more specific than a `le_id` one, so it wins.
  const urlLegalEntityId = urlNursingHomeId
    ? null
    : getQueryParam?.('le_id') ?? null;

  const pinNursingHomeScope = useCallback(
    () => applyScope((current) => ({ ...current, kind: 'nursing_home' })),
    [applyScope]
  );

  const pinLegalEntityScope = useCallback(
    () => applyScope((current) => ({ ...current, kind: 'legal_entity' })),
    [applyScope]
  );

  const nursingHomes = useStoredSelection<NursingHome>({
    idKey: 'selectedNursingHomeId',
    objectKey: 'selectedNursingHome',
    urlId: urlNursingHomeId,
    onUrlIdApplied: pinNursingHomeScope,
  });

  const legalEntities = useStoredSelection<LegalEntity>({
    idKey: 'selectedLegalEntityId',
    objectKey: 'selectedLegalEntity',
    urlId: urlLegalEntityId,
    onUrlIdApplied: pinLegalEntityScope,
  });

  useEffect(() => {
    resources.registerResource(STORAGE_KEY);
  }, [resources]);

  useEffect(() => {
    (async function () {
      try {
        const stored = await resources.get(STORAGE_KEY);
        if (!stored) {
          return;
        }
        // A `nh_id`/`le_id`/`tenant` link pins the scope explicitly; the
        // selections above own it instead of the stored value.
        if (
          getQueryParam?.('nh_id') ||
          getQueryParam?.('le_id') ||
          getQueryParam?.('tenant')
        ) {
          return;
        }
        const parsed = JSON.parse(stored);
        setScopeInternal(
          isStoredScope(parsed) ? { kind: parsed.kind } : DEFAULT_SCOPE
        );
      } finally {
        // Every path above settles the scope, including the ones that keep the
        // default, so the flag belongs here rather than next to the setter.
        setIsScopeRestored(true);
      }
    })();
  }, [resources, getQueryParam]);

  useEffect(() => {
    // A `tenant` link has no id to wait for, so it applies at mount - unless
    // a more specific `nh_id`/`le_id` is also present, in which case the
    // selections above decide.
    if (!getQueryParam || getQueryParam('nh_id') || getQueryParam('le_id')) {
      return;
    }
    if (getQueryParam('tenant')) {
      applyScope((current) => ({ ...current, kind: 'tenant' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setScope = useCallback(
    (next: StoredScope) => applyScope(() => next),
    [applyScope]
  );

  const value = useMemo(
    () => ({
      ...scope,
      nursingHomeList: nursingHomes.list,
      selectedNursingHome: nursingHomes.selected,
      setNursingHomeList: nursingHomes.setList,
      selectNursingHome: nursingHomes.select,
      legalEntityList: legalEntities.list,
      selectedLegalEntity: legalEntities.selected,
      setLegalEntityList: legalEntities.setList,
      selectLegalEntity: legalEntities.select,
      knownLegalEntities,
      setKnownLegalEntities,
      tenant,
      setTenant,
      setScope,
      isScopeRestored,
    }),
    [
      scope,
      nursingHomes,
      legalEntities,
      knownLegalEntities,
      tenant,
      setScope,
      isScopeRestored,
    ]
  );

  return (
    <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>
  );
};
